import React from 'react'
import styled from 'styled-components/macro'
import { FaCheck } from 'react-icons/fa'
import { transparentize } from 'polished'

interface SelectableOptionProps {
  isSelected: boolean
  onClick: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
}

const OptionBlock = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding: 0.375rem;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  max-width: 640px;
  border: 1px solid ${props => props.theme.colors.bg300};
  border-color: ${props =>
    props.isSelected ? props.theme.colors.primary : props.theme.colors.bg300};
  background-color: ${props =>
    props.isSelected
      ? props.theme.colors.secondaryPurple100
      : props.theme.colors.bg0};
  border-radius: ${props => props.theme.rounding.medium};
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text0};

  &:focus {
    border: 1px solid ${props => props.theme.colors.primary};
    box-shadow: 0 0 2px 2px
      ${props => transparentize(0.5, props.theme.colors.primary)};
    outline: none;
  }

  &:hover {
    border-color: #0a1f44;
    box-shadow: 0 0 2px 2px
      ${props => transparentize(0.5, props.theme.colors.primary)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.125rem;
`

const Title = styled.span`
  font-weight: bold;
  margin-right: 0.5rem;
`

const Subtitle = styled.span`
  font-size: 1.2rem;
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Checkmark = styled(FaCheck)`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  color: ${props => props.theme.colors.primary};
  background-color: white;
  border-radius: 50%;
  padding: 0.13rem;
`

export const SelectableOption: React.FC<SelectableOptionProps> = ({
  isSelected,
  onClick,
  title,
  subtitle,
  children,
}) => (
  <OptionBlock isSelected={isSelected} onClick={onClick}>
    {isSelected && <Checkmark />}
    <Header>
      <Title>{title}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </Header>
    {children && <Content>{children}</Content>}
  </OptionBlock>
)
